<template>
    <section class="l2-roadmap" id="roadmap">
        <!-- Animated background particles -->
        <div class="l2-roadmap__particles">
            <div v-for="n in 30" :key="n" class="l2-roadmap__particle" :style="particleStyles[n - 1]"></div>
        </div>

        <!-- Floating orbs -->
        <div class="l2-roadmap__orbs">
            <div class="l2-roadmap__orb l2-roadmap__orb--1"></div>
            <div class="l2-roadmap__orb l2-roadmap__orb--2"></div>
            <div class="l2-roadmap__orb l2-roadmap__orb--3"></div>
        </div>

        <div class="l2-roadmap__container">
            <div class="l2-roadmap__header">
                <h2 class="l2-roadmap__title">
                    The roadmap is alive.<br />
                    <span class="l2-roadmap__title-accent">Built alongside you.</span>
                </h2>
                <p class="l2-roadmap__subtitle">
                    We're not building in a vacuum. These features are shaped by real home businesses, and the list keeps growing based on what you
                    actually need.
                </p>
            </div>

            <!-- Constellation grid -->
            <div class="l2-roadmap__constellation">
                <!-- Connecting lines SVG -->
                <svg class="l2-roadmap__connections" preserveAspectRatio="none">
                    <defs>
                        <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stop-color="var(--l2-accent)" stop-opacity="0.6" />
                            <stop offset="50%" stop-color="var(--l2-accent-secondary)" stop-opacity="0.4" />
                            <stop offset="100%" stop-color="var(--l2-accent)" stop-opacity="0.2" />
                        </linearGradient>
                    </defs>
                </svg>

                <div class="l2-roadmap__grid">
                    <div
                        v-for="(item, index) in roadmapItems"
                        :key="item.title"
                        :class="['l2-roadmap-item', `l2-roadmap-item--${item.size}`, { 'l2-roadmap-item--highlight': item.highlight }]"
                        :style="{ animationDelay: `${index * 0.1}s` }">
                        <!-- Glow effect -->
                        <div class="l2-roadmap-item__glow"></div>

                        <!-- Card content -->
                        <div class="l2-roadmap-item__inner">
                            <!-- Pulse beacon -->
                            <div class="l2-roadmap-item__beacon">
                                <div class="l2-roadmap-item__beacon-dot"></div>
                                <div class="l2-roadmap-item__beacon-ring"></div>
                                <div class="l2-roadmap-item__beacon-ring l2-roadmap-item__beacon-ring--delayed"></div>
                            </div>

                            <!-- Icon -->
                            <div class="l2-roadmap-item__icon">
                                <v-icon :icon="item.icon" :size="item.size === 'large' ? 32 : 24" />
                            </div>

                            <!-- Content -->
                            <div class="l2-roadmap-item__content">
                                <h3 class="l2-roadmap-item__title">{{ item.title }}</h3>
                                <p class="l2-roadmap-item__description">{{ item.description }}</p>

                                <div v-if="item.size === 'featured'" class="mt-4">
                                    <v-btn
                                        variant="outlined"
                                        size="small"
                                        rounded="lg"
                                        prepend-icon="mdi-lightbulb-on-outline"
                                        :color="theme === 'light' ? undefined : '#ffffff'"
                                        @click="emit('contact')">
                                        Submit Idea
                                    </v-btn>
                                </div>
                            </div>

                            <!-- Corner accent -->
                            <div class="l2-roadmap-item__corner"></div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="l2-roadmap__cta">
                <v-btn class="l2-roadmap__cta-button" size="large" variant="outlined" :color="notForYouButtonColor" @click="openNotForYou">
                    Not for you?
                </v-btn>
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import type { LandingTheme } from '@/pages/Landing/composables/useLandingTheme';
    import { createLandingParticleStyles } from '@/pages/Landing/utils/createLandingParticleStyles';
    import { computed } from 'vue';

    const props = defineProps<{
        theme: LandingTheme;
    }>();

    const emit = defineEmits<{
        (e: 'not-for-you'): void;
        (e: 'contact'): void;
    }>();

    const particleStyles = createLandingParticleStyles(30);

    const notForYouButtonColor = computed(() => (props.theme === 'light' ? undefined : '#ffffff'));

    const openNotForYou = () => {
        emit('not-for-you');
    };

    const roadmapItems = [
        {
            icon: 'mdi-camera-document',
            title: 'Receipt Scanning',
            description: `Snap a photo of a receipt and we'll turn it into structured purchases with items, quantities, and costs so inventory and ingredient pricing stay accurate with way less manual typing.`,
            size: 'small',
            highlight: false,
        },
        {
            icon: 'mdi-calendar',
            title: 'Calendar Connections',
            description: `Connect your calendar (Google, Apple, Outlook) so prep, pickups, and order events can stay in sync without double entry.`,
            size: 'small',
            highlight: false,
        },
        {
            icon: 'mdi-label-multiple',
            title: 'Label Generator',
            description: `Generate clean, consistent labels for your products with names, dates, batch info, allergens, ingredients, and whatever else you need, ready to print and stick.`,
            size: 'small',
            highlight: false,
        },
        {
            icon: 'mdi-credit-card-outline',
            title: 'Payments',
            description: `Accept payments so your buyers can pay invoices online and view their full payment history, you spend less time chasing payments and more time making.`,
            size: 'small',
            highlight: false,
        },
        {
            icon: 'mdi-microphone',
            title: 'Siri Commands',
            description: `Hands-free voice commands like "Hey Siri, add Sugar to my restock list", "Hey Siri mark today's Coffee Shop order complete", "Hey Siri, how much sugar do I have on hand?", and "Hey Siri, How many Cheesecakes do I have in stock?"`,
            size: 'small',
            highlight: false,
        },
        {
            icon: 'mdi-cellphone',
            title: 'Mobile Apps',
            description: `Native mobile apps for iOS and Android so you can manage orders, prep, pickups, and inventory from the kitchen without juggling tabs.`,
            size: 'small',
            highlight: false,
        },
        {
            icon: 'mdi-database-import',
            title: 'Imports',
            description: `Bring your data in quickly, including recipes, inventory, customers, and orders, so switching (or consolidating) doesn't mean starting from scratch.`,
            size: 'small',
            highlight: false,
        },
        {
            icon: 'mdi-lightbulb-on',
            title: 'Your Ideas',
            description: `Have a feature you wish existed? Tell us. We’re building Mix n’ Batch alongside real home businesses, and the roadmap is customer-led, so we build what helps your business most.`,
            size: 'featured',
            highlight: true,
        },
    ];
</script>

<style lang="scss" scoped>
    .l2-roadmap {
        position: relative;
        padding: var(--l2-section-padding-y) 24px;
        background: var(--l2-bg);
        overflow: hidden;

        // Base gradient
        &::before {
            content: '';
            position: absolute;
            inset: 0;
            background:
                radial-gradient(ellipse 80% 50% at 50% 0%, rgba(167, 139, 250, 0.12), transparent 50%),
                radial-gradient(ellipse 60% 40% at 100% 50%, rgba(103, 232, 249, 0.08), transparent 50%),
                radial-gradient(ellipse 60% 40% at 0% 80%, rgba(167, 139, 250, 0.1), transparent 50%);
            pointer-events: none;
        }

        &__particles {
            position: absolute;
            inset: 0;
            pointer-events: none;
            overflow: hidden;
        }

        &__particle {
            position: absolute;
            background: var(--l2-accent);
            border-radius: 50%;
            animation: float-particle linear infinite;
            box-shadow: 0 0 6px var(--l2-accent);
            will-change: transform, opacity;
        }

        &__orbs {
            position: absolute;
            inset: 0;
            pointer-events: none;
            overflow: hidden;
        }

        &__orb {
            position: absolute;
            border-radius: 50%;
            filter: blur(100px);
            animation: orbit 20s ease-in-out infinite;

            &--1 {
                width: 600px;
                height: 600px;
                background: radial-gradient(circle, rgba(167, 139, 250, 0.2), transparent 60%);
                top: -200px;
                right: -200px;
                animation-delay: 0s;
            }

            &--2 {
                width: 400px;
                height: 400px;
                background: radial-gradient(circle, rgba(103, 232, 249, 0.15), transparent 60%);
                bottom: 20%;
                left: -150px;
                animation-delay: -7s;
            }

            &--3 {
                width: 500px;
                height: 500px;
                background: radial-gradient(circle, rgba(233, 213, 255, 0.1), transparent 60%);
                bottom: -200px;
                right: 20%;
                animation-delay: -14s;
            }
        }

        &__container {
            position: relative;
            max-width: 1280px;
            margin: 0 auto;
            z-index: 1;
        }

        &__cta {
            margin-top: 32px;
            text-align: center;
        }

        &__cta-button {
            border-color: var(--l2-text-subtle);
            color: var(--l2-text-subtle);
            text-transform: none;
            letter-spacing: normal;
            min-height: 44px;
            padding-inline: 18px;

            &:hover {
                border-color: var(--l2-text);
                color: var(--l2-text);
            }
        }

        &__header {
            text-align: center;
            margin-bottom: 70px;
            max-width: 700px;
            margin-left: auto;
            margin-right: auto;
        }

        &__kicker {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 0.875rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.15em;
            color: var(--l2-accent);
            margin-bottom: 20px;
            padding: 8px 20px;
            background: var(--l2-accent-soft);
            border-radius: 100px;
            border: 1px solid var(--l2-border);
        }

        &__title {
            font-size: clamp(2rem, 5vw, 3.5rem);
            font-weight: 800;
            line-height: 1.15;
            color: var(--l2-text);
            margin-bottom: 24px;
            letter-spacing: -0.02em;
        }

        &__title-accent {
            color: var(--landing-muted);
        }

        &__subtitle {
            font-size: 1.125rem;
            line-height: 1.7;
            color: var(--landing-muted);
            margin: 0;
        }

        &__constellation {
            position: relative;
        }

        &__connections {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 0;
        }

        &__grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 20px;
            position: relative;
            z-index: 1;

            @media (min-width: 600px) {
                grid-template-columns: repeat(2, 1fr);
            }

            @media (min-width: 960px) {
                grid-template-columns: repeat(12, 1fr);
                gap: 24px;
            }
        }
    }

    @media (max-width: 600px) {
        .l2-roadmap {
            &__header {
                margin-bottom: 40px;
            }

            &__title {
                margin-bottom: 16px;
            }
        }
    }

    .l2-roadmap-item {
        position: relative;
        animation: card-appear 0.8s ease-out forwards;
        opacity: 0;

        // Grid sizing for desktop
        @media (min-width: 960px) {
            &--large {
                grid-column: span 6;
            }

            &--medium {
                grid-column: span 4;
            }

            &--small {
                grid-column: span 3;
            }

            &--featured {
                grid-column: span 3;
            }
        }

        @media (min-width: 600px) and (max-width: 959px) {
            &--featured {
                grid-column: span 1;
            }
        }

        &__glow {
            display: none;
        }

        &__inner {
            position: relative;
            height: 100%;
            padding: 28px;
            background: var(--l2-card-glass-bg);
            border: 1px solid var(--l2-border-subtle);
            border-radius: var(--l2-radius);
            backdrop-filter: blur(20px);
            overflow: hidden;

            @media (min-width: 600px) {
                padding: 32px;
            }
        }

        &__beacon {
            position: absolute;
            top: 20px;
            right: 20px;
            width: 12px;
            height: 12px;
        }

        &__beacon-dot {
            position: absolute;
            inset: 3px;
            background: var(--l2-accent);
            border-radius: 50%;
            box-shadow: 0 0 10px var(--l2-accent);
        }

        &__beacon-ring {
            position: absolute;
            inset: 0;
            border: 2px solid var(--l2-accent);
            border-radius: 50%;
            opacity: 0;
        }

        &--highlight &__beacon-dot {
            background: var(--l2-accent-secondary);
            box-shadow: 0 0 10px var(--l2-accent-secondary);
        }

        &--highlight &__beacon-ring {
            border-color: var(--l2-accent-secondary);
        }

        &:hover &__beacon-ring {
            animation: beacon-pulse 2s ease-out infinite;

            &.l2-roadmap-item__beacon-ring--delayed {
                animation-delay: 0.5s;
            }
        }

        &__icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 56px;
            height: 56px;
            background: linear-gradient(135deg, var(--l2-accent-soft), rgba(103, 232, 249, 0.1));
            border: 1px solid var(--l2-border);
            border-radius: 16px;
            margin-bottom: 20px;

            .v-icon {
                color: var(--l2-accent) !important;
            }
        }

        &--large &__icon {
            width: 64px;
            height: 64px;
            border-radius: 18px;
        }

        &__content {
            position: relative;
            z-index: 1;
        }

        &__title {
            font-size: 1.25rem;
            font-weight: 700;
            color: var(--l2-text);
            margin: 0 0 10px;
        }

        &--large &__title {
            font-size: 1.5rem;
        }

        &__description {
            font-size: 0.9375rem;
            line-height: 1.65;
            color: var(--l2-text-muted);
            margin: 0;
        }

        &--large &__description {
            font-size: 1rem;
        }

        &__corner {
            display: none;
        }

        // Featured card special styling
        &--featured {
            .l2-roadmap-item__inner {
                background: linear-gradient(135deg, rgba(167, 139, 250, 0.15), rgba(103, 232, 249, 0.1));
                border-color: var(--l2-border);
            }

            .l2-roadmap-item__icon {
                background: linear-gradient(135deg, var(--l2-accent), var(--l2-accent-secondary));

                .v-icon {
                    color: var(--l2-bg) !important;
                }
            }
        }
    }

    // Animations
    @keyframes float-particle {
        0%,
        100% {
            transform: translate(0, 0) scale(1);
        }
        25% {
            transform: translate(10px, -20px) scale(1.1);
        }
        50% {
            transform: translate(-5px, -40px) scale(0.9);
        }
        75% {
            transform: translate(-15px, -20px) scale(1.05);
        }
    }

    @keyframes orbit {
        0%,
        100% {
            transform: translate(0, 0) scale(1);
        }
        25% {
            transform: translate(30px, -20px) scale(1.05);
        }
        50% {
            transform: translate(0, 30px) scale(0.95);
        }
        75% {
            transform: translate(-30px, -10px) scale(1.02);
        }
    }

    @keyframes beacon-pulse {
        0% {
            transform: scale(1);
            opacity: 0.8;
        }
        100% {
            transform: scale(2.5);
            opacity: 0;
        }
    }

    @keyframes card-appear {
        0% {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
        }
        100% {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }
</style>
